import type { InputSource, Intent } from '../input/types'

export type EntityKind =
  | 'player'
  | 'enemy'
  | 'projectile'
  | 'pickup'
  | 'generator'
  | 'boss'
  | 'corpse'
  | 'fx'

/** In-fiction: the player is a Participant; everything hostile is Network property. */
export type Faction = 'participant' | 'network'

export type EnemyType = 'husk' | 'sentry' | 'static' | 'foreman' | 'auditor'
export type PickupType = 'food' | 'key' | 'magic' | 'treasure' | 'archive' | 'armament'
export type ArmamentId = 'scatter' | 'lance' | 'seam' | 'suppressor'

export interface AIState {
  state: string
  t: number
  /** current steering target (world coords) */
  tx: number
  ty: number
  cooldown: number
  /** misc per-behavior scratch */
  data: Record<string, number>
}

export interface PlayerState {
  intent: Intent
  source: InputSource
  magic: number
  keys: number
  fireT: number
  contactT: number
  starvingEventT: number
  /** found sanctioned armament (temporary weapon); undefined = standard-issue blaster */
  armament?: { id: ArmamentId; charge: number } | undefined
  /** alt-fire (heavy bolt) cooldown timer */
  altT: number
}

export interface GeneratorState {
  genType: Exclude<EnemyType, 'auditor'>
  interval: number
  t: number
  /** total remaining spawns; Infinity = uncapped */
  budget: number
  aliveCap: number
  /** foreman aura multiplier applied this tick */
  boost: number
}

export interface BossPhaseDef {
  /** enter when hp fraction <= threshold */
  threshold: number
  /** attack ids cycled within the phase */
  attacks: string[]
  /** seconds between attack cycles */
  attackInterval: number
  /** generator spawns during this phase */
  addSpawning?: { type: Exclude<EnemyType, 'auditor'>; interval: number; aliveCap: number }
}

export interface BossDef {
  id: string
  name: string
  hp: number
  speed: number
  radius: number
  renderId: string
  phases: BossPhaseDef[]
  /** tuning multipliers applied to attack params */
  params: Record<string, number>
}

export type BossAttackStage = 'idle' | 'telegraph' | 'attack' | 'recover'

export interface BossState {
  def: BossDef
  phase: number
  stage: BossAttackStage
  stageT: number
  attackIndex: number
  currentAttack: string
  /** attack-scratch vector (e.g. charge direction) */
  ax: number
  ay: number
  addT: number
  touchT: number
}

/**
 * Data-oriented entity: flat struct, optional component groups, no behavior.
 * DECISION: a disciplined single-interface entity model instead of a full
 * archetype ECS — systems still operate over component presence, but the
 * entity count (< ~300 live) doesn't justify archetype machinery in v1.
 */
export interface Entity {
  id: number
  kind: EntityKind
  // Transform
  x: number
  y: number
  facing: number
  // previous position (render interpolation)
  px: number
  py: number
  // Velocity
  vx: number
  vy: number
  // Collider
  radius: number
  /** blocked by solid tiles? (statics/ghosts are not) */
  wallClipped: boolean
  // Renderable
  renderId: string
  flash: number
  spawnT: number
  dead: boolean
  // Health (optional component)
  hp?: number
  maxHp?: number
  /** SURPLUS METABOLISM: decaying damage buffer absorbed before hp */
  shield?: number
  invuln?: number
  faction?: Faction
  // Enemy
  etype?: EnemyType
  ai?: AIState
  contactT?: number
  parentGen?: number
  /** band-scaled movement multiplier fixed at spawn (see enemies.speedBandScale) */
  speedMul?: number
  /** hostiles idle until they sense the participant (see enemies.wake); statics/auditors are born aware */
  awake?: boolean
  // Projectile
  proj?: { damage: number; faction: Faction; pierce?: boolean; bounces?: number; hitIds?: number[] }
  lifetime?: number
  // Pickup
  pickup?: { ptype: PickupType; value: number; armament?: ArmamentId }
  // Generator
  gen?: GeneratorState
  // Boss
  boss?: BossState
  // Player
  player?: PlayerState
  // tile-effect cooldowns
  trapT?: number
  teleT?: number
  // Corpse (foreman resurrection target)
  corpseOf?: Exclude<EnemyType, 'auditor'>
}

let nextId = 1

/**
 * Reset the id counter at run start: AI behaviors key off e.id (strafe
 * direction, jitter phase), so same-seed runs must hand out the same ids.
 */
export function resetEntityIds(): void {
  nextId = 1
}

export function makeEntity(kind: EntityKind, x: number, y: number, radius: number, renderId: string): Entity {
  return {
    id: nextId++,
    kind,
    x,
    y,
    px: x,
    py: y,
    facing: 0,
    vx: 0,
    vy: 0,
    radius,
    wallClipped: true,
    renderId,
    flash: 0,
    spawnT: 0,
    dead: false
  }
}
